import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import SiteFooter from "../components/SiteFooter";
import { LogoWordmark } from "../components/Logo";
import {
  NorthlineDesktop, NorthlinePhone,
  HawthornDesktop, HawthornPhone,
  CastleDesktop, CastlePhone,
  InvoiceCard, BrandSystem,
} from "../components/BusinessMocks";

const Section = ({ n, name, note, children }) => (
  <section className="py-16 lg:py-24 border-b border-[color:var(--border-soft)]">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <div className="flex items-center gap-4 mb-8">
        <span className="grid-ref">§ {n}</span>
        <span className="h-px flex-1 bg-[color:var(--border)]" />
        <span className="grid-ref">{name}</span>
      </div>
      {note && <p className="text-[13px] text-[color:var(--muted)] mb-8 max-w-2xl">{note}</p>}
      {children}
    </div>
  </section>
);

const DesignPreviewPage = () => (
  <div className="min-h-screen bg-[color:var(--bg)] overflow-x-hidden" data-testid="design-preview-page">
    {/* Preview-only header */}
    <header className="border-b border-[color:var(--border-soft)] bg-[color:var(--bg)] sticky top-0 z-40 backdrop-blur">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/"><LogoWordmark size={28} /></Link>
        <div className="flex items-center gap-3">
          <span className="stamp stamp-accent">Design Preview · Awaiting Approval</span>
          <Link to="/" className="btn-ghost text-[13px]"><ArrowLeft size={13} /> Back to live site</Link>
        </div>
      </div>
    </header>

    {/* Intro */}
    <section className="pt-16 lg:pt-24 pb-8">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <span className="grid-ref">§ 00 / For approval</span>
        <h1 className="h-xl text-[color:var(--ink)] mt-4 max-w-4xl">
          New website mock-up graphics. <span className="font-serif-italic">Six compositions.</span>
        </h1>
        <p className="copy-lg text-[color:var(--muted)] mt-6 max-w-2xl">
          Every mock-up below is real HTML/CSS — no images of interfaces, no lorem ipsum. Text stays crisp at any size. Three fictional businesses (Northline Plumbing, Hawthorn Electrical) plus Castle Groundcare as a real client. Nothing is live on the homepage yet.
        </p>
      </div>
    </section>

    {/* 1 — Desktop hero composition */}
    <Section
      n="01"
      name="Hero composition · desktop"
      note="Replaces the current Halliday & Sons hero on the homepage. One connected case-study frame — desktop screen dominant, mobile alongside, branded invoice tucked behind. No overlap on the underlying copy."
    >
      <div className="rounded-sm border border-[color:var(--border-soft)] bg-[color:var(--bg-paper)] p-6 lg:p-10">
        <div className="flex items-center justify-between mb-6">
          <span className="grid-ref">FIG. 01 · Northline Plumbing &amp; Heating</span>
          <span className="stamp stamp-accent">Example ReadyTrades concept</span>
        </div>

        <div className="relative grid grid-cols-12 gap-4 items-end">
          <div className="col-span-12 md:col-span-9 relative z-10">
            <NorthlineDesktop />
          </div>
          <div className="col-span-6 md:col-span-3 relative z-20 md:-ml-16 md:mb-[-3rem]">
            <NorthlinePhone />
          </div>
          <div className="col-span-6 md:col-span-4 md:absolute md:-bottom-8 md:right-[38%] z-0 md:opacity-95">
            <InvoiceCard business="northline" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-16 pt-6 border-t border-[color:var(--border-soft)] text-[12px] text-[color:var(--muted)]">
          <div><p className="grid-ref mb-1">A · Trade</p>Plumbing &amp; Heating</div>
          <div><p className="grid-ref mb-1">B · Location</p>Liverpool · Merseyside</div>
          <div><p className="grid-ref mb-1">C · Direction</p>Navy + safety-red, family-run heritage feel</div>
        </div>
      </div>
    </Section>

    {/* 2 — Mobile hero */}
    <Section
      n="02"
      name="Hero composition · mobile"
      note="Below 1024px the three artefacts stack cleanly. Phone leads (it's the phone-first case study), desktop crops to a wide preview, invoice becomes a footer card."
    >
      <div className="rounded-sm border border-[color:var(--border-soft)] bg-[color:var(--bg-paper)] p-6 lg:p-10 flex flex-col items-center gap-6 max-w-md mx-auto">
        <span className="grid-ref">FIG. 02 · Mobile stack</span>
        <NorthlinePhone className="w-full max-w-[240px]" />
        <div className="w-full rounded-sm overflow-hidden border border-[color:var(--border)] bg-white" style={{ aspectRatio: '16/8' }}>
          <div className="scale-[0.55] origin-top-left w-[181%]">
            <NorthlineDesktop />
          </div>
        </div>
        <InvoiceCard business="northline" className="w-full max-w-[280px]" />
      </div>
    </Section>

    {/* 3 — Full Northline homepage */}
    <Section
      n="03"
      name="Full homepage · Northline Plumbing &amp; Heating"
      note="Complete website design: navigation with click-to-call, hero with photograph, four service cards, verified review, areas covered. This is the artefact featured in the hero above — shown here at full-size."
    >
      <div className="flex items-center justify-between mb-4">
        <span className="grid-ref">FIG. 03 · northlineplumbing.co.uk</span>
        <span className="stamp stamp-accent">Example ReadyTrades concept</span>
      </div>
      <NorthlineDesktop />
    </Section>

    {/* 4 — Full Hawthorn homepage */}
    <Section
      n="04"
      name="Full homepage · Hawthorn Electrical"
      note="Distinct art direction from Northline: charcoal + cream + controlled yellow, numbered ledger for services, editorial dashes as annotations. Photograph anchors the right side of the hero."
    >
      <div className="flex items-center justify-between mb-4">
        <span className="grid-ref">FIG. 04 · hawthornelectrical.co.uk</span>
        <span className="stamp stamp-accent">Example ReadyTrades concept</span>
      </div>
      <HawthornDesktop />
    </Section>

    {/* 5 — Castle portfolio presentation */}
    <Section
      n="05"
      name="Portfolio tile · Castle Groundcare (real client)"
      note="Case-study presentation for the /our-clients page. Castle's photography sits full-bleed in the hero; the portfolio tile below sits alongside two additional deliverables (mobile + invoice)."
    >
      <div className="rounded-sm border border-[color:var(--border-soft)] bg-[color:var(--bg-paper)] p-6 lg:p-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="grid-ref">FIG. 05 · castlegroundcare.co.uk</span>
            <p className="font-display text-[24px] text-[color:var(--ink)] mt-2">Castle Groundcare</p>
            <p className="text-[12px] text-[color:var(--muted)]">Landscaping &amp; grounds maintenance · Great Dunmow, Essex</p>
          </div>
          <div className="text-right">
            <span className="stamp stamp-ink">Real client project</span>
            <p className="text-[10px] text-[color:var(--muted)] mt-2 italic max-w-[240px]">
              Placeholder photography — Castle's real photographs to be inserted before publish.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-12 md:col-span-8">
            <CastleDesktop />
          </div>
          <div className="col-span-6 md:col-span-2 flex justify-center">
            <CastlePhone />
          </div>
          <div className="col-span-6 md:col-span-2">
            <InvoiceCard business="castle" />
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-[color:var(--border-soft)] flex items-center justify-between">
          <div>
            <p className="grid-ref">Short description</p>
            <p className="text-[13px] text-[color:var(--muted)] mt-1 max-w-2xl">
              A three-decade grounds-care family firm serving high-end residential estates and commercial properties across Essex. The site reads as considered and premium — deep forest palette, landscape-led composition.
            </p>
          </div>
          <a
            href="https://www.castlegroundcare.co.uk"
            target="_blank"
            rel="noreferrer"
            className="btn-navy text-[13px]"
          >
            View Example <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </Section>

    {/* 6 — Connected brand/website/Google/invoice system */}
    <Section
      n="06"
      name="Connected visual · Your brand · Your website · Your Google · Your quotes"
      note="Replaces the abstract SaaS-style 'What You Get' feature grid. Every artefact belongs to the same fictional project (Northline), so it reads as 'this is what you actually receive' — not four random UI cards."
    >
      <BrandSystem />
    </Section>

    {/* Approval footer */}
    <section className="py-16 lg:py-24">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
        <span className="grid-ref">§ 07 / Sign-off</span>
        <h2 className="h-lg text-[color:var(--ink)] mt-4 max-w-3xl mx-auto">
          Happy with the direction? <span className="font-serif-italic">Approve and I'll roll it across the homepage.</span>
        </h2>
        <p className="copy-lg text-[color:var(--muted)] mt-5 max-w-xl mx-auto">
          The current homepage stays untouched until you're happy with what you see above. Tweak anything you don't love and I'll rework it.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/" className="btn-navy">
            <ArrowLeft size={14} /> Back to live homepage
          </Link>
          <a href="mailto:contact@readytrades.co.uk" className="btn-primary">
            Send tweaks / approve <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>

    <SiteFooter />
  </div>
);

export default DesignPreviewPage;
